import React from "react"
import { CreditCard } from "lucide-react"
import TopicPageLayout from "../topic-page-layout"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../ui/accordion"

const Payments = () => {
  const faqItems = [
    {
      question: "When will I receive my payments?",
      answer:
        "Payments are processed on the 15th of each month for the previous month's earnings, provided you've reached the minimum payout threshold of $50. It typically takes 3-5 business days for the funds to appear in your account."
    },
    {
      question: "What payment methods are supported?",
      answer:
        "We support direct bank deposits, PayPal, and Stripe. Depending on your location, additional local payment methods may be available in your dashboard."
    },
    {
      question: "How do I set up my tax information?",
      answer:
        "Go to your Account Settings > Payments > Tax Information and follow the instructions. For US-based creators, you'll need to submit a W-9 form. For international creators, you'll need to complete a W-8BEN form."
    },
    {
      question: "Are there any fees for receiving payments?",
      answer:
        "We charge a standard 5% platform fee on all earnings. Additional payment processor fees may apply depending on your chosen payment method (typically 2-3%)."
    },
    {
      question: "How do I issue refunds to my supporters?",
      answer:
        "You can issue refunds from your dashboard under Transactions. Select the specific transaction and click the 'Issue Refund' button. Refunds are processed within 5-7 business days."
    }
  ]

  return (
    <TopicPageLayout
      title="Payments"
      description="Learn how to get paid, manage your earnings, set up payment methods, and understand our fee structure."
      icon={<CreditCard className="h-8 w-8 text-[#3D2A60]" />}
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Payment Overview
          </h2>
          <p className="text-gray-600 mb-6">
            We've designed our payment system to be transparent, secure, and
            efficient. As a creator, understanding how payments work is
            essential to maximize your earnings and plan your finances.
          </p>

          <div className="bg-[#F4F0FA]/50 p-6 rounded-lg mb-6">
            <h3 className="text-lg font-medium mb-2 text-[#3D2A60]">
              Payment Schedule
            </h3>
            <p className="text-gray-600">
              Payments are processed once a month on the 15th for the previous
              month's earnings. The minimum payout threshold is $50. If your
              earnings are below this threshold, they will roll over to the
              next payment period.
            </p>
          </div>

          <div className="bg-[#F4F0FA]/50 p-6 rounded-lg">
            <h3 className="text-lg font-medium mb-2 text-[#3D2A60]">
              Fee Structure
            </h3>
            <p className="text-gray-600 mb-4">
              Our platform charges the following fees:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li>5% platform fee on all earnings</li>
              <li>
                Payment processor fees (2-3% depending on the payment method)
              </li>
              <li>Currency conversion fees (if applicable)</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Setting Up Your Payment Methods
          </h2>
          <p className="text-gray-600 mb-6">
            To receive payments, you need to set up at least one payment
            method in your dashboard. We recommend setting up multiple payment
            methods as backup options.
          </p>

          <div className="space-y-4">
            <div className="border-l-4 border-[#3D2A60] pl-4">
              <h3 className="text-lg font-medium mb-2 text-[#3D2A60]">
                Direct Bank Deposit
              </h3>
              <p className="text-gray-600">
                The most common and usually the fastest method. You'll need to
                provide your bank account details, including routing number
                and account number.
              </p>
            </div>

            <div className="border-l-4 border-[#3D2A60] pl-4">
              <h3 className="text-lg font-medium mb-2 text-[#3D2A60]">
                PayPal
              </h3>
              <p className="text-gray-600">
                Connect your PayPal account for easy transfers. Make sure your
                PayPal email matches the email you use for our platform.
              </p>
            </div>

            <div className="border-l-4 border-[#3D2A60] pl-4">
              <h3 className="text-lg font-medium mb-2 text-[#3D2A60]">
                Stripe
              </h3>
              <p className="text-gray-600">
                For international creators, Stripe offers flexibility with
                currency options and competitive conversion rates.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-[#3D2A60]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-gray-600">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </TopicPageLayout>
  )
}

export default Payments
